import React, { useEffect, useRef, useState } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import {
  faClose,
  faFileSignature,
  faKey,
  faMailBulk,
  faMoneyBill,
  faPenToSquare,
  faPercentage,
} from "@fortawesome/free-solid-svg-icons"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import InputField from "../../../components/InputField/InputField"
import GetSpace from "../../../utils/space"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { PersonComponent } from "../../../components/Person/PersonComponent"
import { Employee } from "../../../Objects/Employee/Employee"
import { useCreateEmployee } from "../../../hooks/Employee/useCreateEmployee"
import { InputPhotoWithContainer } from "../../../components/InputPhoto/InputPhoto"
import { useSaveEmployee } from "../../../hooks/Employee/useSaveEmployee"
import { useFileImage } from "../../../hooks/useFileImage"
import { useRegister } from "../../../hooks/Register/useRegister"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import { useEmployerLogged } from "../../../context/useEmployeeContext"
import { useGetEmployee } from "../../../hooks/Employee/useGetEmployee"
import { useError } from "../../../context/useErrorContext"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { INPUT_SIZE } from "../../../configuration/InputSize"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { useTranslation } from "react-i18next"
import { ChangePasswordPopup } from "./ChangePasswordPupop"
import { EmployeePermissions } from "./Components/EmployeePermissions"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { Permissions } from "../../../Enum/PermissionsType"
import { CanView } from "../../../components/CanView/CanView"
import { AddressEntity } from "../../../Objects/Person/AddressEntity"
import { AddressesManager } from "../../../components/Address/AddressesManager"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"

interface EmployeePupopProbs {
  title: string
  isCreateMaster: boolean
  employee?: Employee | undefined | null
  onClonse: () => void
}

export const EmployeePupop: React.FC<EmployeePupopProbs> = ({ title, employee, isCreateMaster, onClonse }) => {
  const { t } = useTranslation("employee")

  const [enableChangePassword, setChangePassword] = useState(false)

  const [permissions, setPermissions] = useState<string[]>(employee?.permissions ?? [])

  const [isEditeMode] = useState(employee)

  const { fileImage, setFileImage } = useFileImage()

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  const { handleGetEmployee } = useGetEmployee()

  const { setError } = useError()

  const canEdite = !usePermission(Permissions.CanEditeEmployee)

  const [addresses, setAddresses] = useState<AddressEntity[]>(() => employee?.personEntity?.addresses ?? [])

  const {
    password,
    salaryFixed,
    percentCommission,
    companyFunction: functionE,
    status,
    username,
    handleUsernameChange,
    handlePasswordChange,
    handleSalaryFixedChange,
    handlePercentCommissionChange,
    handleCompanyFunctionChange,
    handleSubmitForm,
    handleInitialValue,
    handleStatusChange,
  } = useCreateEmployee(isCreateMaster)

  const personRef = useRef<any>(null)

  const { handleSaveEmployee } = useSaveEmployee()

  useEffect(() => {
    handleInitialValue(employee)
  }, [employee])

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    const isPersonValid = personRef.current?.validate()

    const isEmployeeValid = handleSubmitForm()

    if (!isPersonValid || !isEmployeeValid) {
      setError(t("general:label_title_error"), t("general:label_description_error"))
      return
    }

    isPersonValid.addresses = addresses
    isEmployeeValid.personEntity = isPersonValid

    handleSaveEmployee(
      isEmployeeValid,
      permissions,
      () => {
        onClonse()
        handleGetEmployee(() => {})
      },
      fileImage,
    )
  }

  return (
    <div className={defaultStyle.overlay}>
      <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
        <div className={defaultStyle.alert}>
          <TitlePageResponsiveIconSmall
            title={title}
            icon={faPenToSquare}
            iconColor={THEME.colors.iconBGColor}
            children={
              <div className={defaultStyle.ButtonsActions}>
                {isEditeMode && !isEditeMode.isMaster ? (
                  <div style={{ width: "100px", marginRight: "5px" }}>
                    <GenericDropDown
                      data={Object.mapEnumToStrings(EEnableStatusType, {
                        exclude: [EEnableStatusType.All],
                      })}
                      initialState={status}
                      handlerFuncion={handleStatusChange}
                      disable={canEdite}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.employees.employeePupop.select.select"
                    />

                    <GetSpace right={10} />
                  </div>
                ) : null}
                <PopupHelpButton
                  helpKey="pages.mystore.employees.employeepupop.1"
                  variant="global"
                  title={t("helpPopup:global.employee.title")}
                  description={t("helpPopup:global.employee.description")}
                  bullets={t("helpPopup:global.employee.bullets", { returnObjects: true }) as string[]}
                />

                <GetSpace right={5} />

                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClonse}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.myRestaurant.employees.employeePupop.button.onClonse"
                />
              </div>
            }
          />

          <GetSpace top={5} />

          <div className="w-full flex flex-col items-center">
            <InputPhotoWithContainer
              handleFileSelected={setFileImage}
              disable={canEdite}
              initialPhoto={employee?.iconUrl}
              imageProfile="employee"
            />

            <div className={defaultStyle.divisionBar}></div>
          </div>

          <form onSubmit={handleSubmit}>
            {!employee?.isMaster && (
              <>
                <div className="w-full">
                  <div className="p-[15px]">
                    {!isEditeMode && (
                      <>
                        <InputField
                          maxLength={INPUT_SIZE.EMPLOYEE.userName}
                          type="text"
                          disable={canEdite}
                          placeholder={t("label_username_place_holder")}
                          onInvalidMessage={t("label_username_onInvalidMessage")}
                          icon={faMailBulk}
                          value={username}
                          onChange={handleUsernameChange}
                          onInput={(e) => {
                            let value = e.currentTarget.value

                            value = value.replace(/\s+/g, "")

                            value = value.normalize("NFD").replace(/[\u0300-\u036f]/g, "")

                            value = value.toLowerCase()

                            value = value.replace(/[^a-z0-9_.]/g, "")

                            e.currentTarget.value = value
                          }}
                          required
                          descriptionKey="interactionDescriptions:pages.myRestaurant.employees.employeePupop.input.usernamePlaceHolder"
                        />

                        <GetSpace top={10} />

                        <InputField
                          maxLength={INPUT_SIZE.EMPLOYEE.password}
                          type="password"
                          disable={canEdite}
                          placeholder={t("label_password_place_holder")}
                          onInvalidMessage={t("label_password_onInvalidMessage")}
                          icon={faKey}
                          value={password}
                          onChange={handlePasswordChange}
                          required
                          descriptionKey="interactionDescriptions:pages.myRestaurant.employees.employeePupop.input.passwordPlaceHolder"
                        />
                      </>
                    )}
                    {
                      <>
                        <GetSpace top={10} />

                        <InputField
                          maxLength={INPUT_SIZE.EMPLOYEE.position}
                          type="text"
                          disable={canEdite}
                          placeholder={t("label_function_place_holder")}
                          onInvalidMessage={t("label_function_onInvalidMessage")}
                          icon={faFileSignature}
                          value={functionE}
                          onChange={handleCompanyFunctionChange}
                          descriptionKey="interactionDescriptions:pages.myRestaurant.employees.employeePupop.input.functionPlaceHolder"
                        />

                        <GetSpace top={10} />

                        <InputField
                          maxLength={INPUT_SIZE.EMPLOYEE.salary}
                          type="number"
                          placeholder={t("label_salary_place_holder")}
                          onInvalidMessage={t("label_salary_onInvalidMessage")}
                          icon={faMoneyBill}
                          value={salaryFixed}
                          disable={canEdite}
                          required
                          onChange={handleSalaryFixedChange}
                          descriptionKey="interactionDescriptions:pages.myRestaurant.employees.employeePupop.input.salaryPlaceHolder"
                        />

                        <GetSpace top={10} />

                        <InputField
                          maxLength={INPUT_SIZE.EMPLOYEE.comission}
                          type="number"
                          placeholder={t("label_commission_place_holder")}
                          onInvalidMessage={t("label_commission_onInvalidMessage")}
                          icon={faPercentage}
                          disable={canEdite}
                          value={percentCommission}
                          required
                          onChange={handlePercentCommissionChange}
                          descriptionKey="interactionDescriptions:pages.myRestaurant.employees.employeePupop.input.commissionPlaceHolder"
                        />

                        <GetSpace top={10} />
                      </>
                    }
                  </div>
                </div>

                <div className={defaultStyle.divisionBar}></div>

                <PersonComponent
                  initExpasive={true}
                  disable={canEdite}
                  personEntity={employee?.personEntity}
                  headerActions={
                    <PopupHelpButton
                      helpKey="pages.mystore.employees.employeepupop.2"
                      title={t("helpPopup:sections.employee.personalData.title")}
                      description={t("helpPopup:sections.employee.personalData.description")}
                      bullets={
                        t("helpPopup:sections.employee.personalData.bullets", { returnObjects: true }) as string[]
                      }
                    />
                  }
                  ref={personRef}
                />

                <div className={defaultStyle.divisionBar}></div>

                <AddressesManager
                  value={addresses}
                  onChange={setAddresses}
                  disable={canEdite}
                  headerActions={
                    <PopupHelpButton
                      helpKey="pages.mystore.employees.employeepupop.3"
                      title={t("helpPopup:sections.employee.addresses.title")}
                      description={t("helpPopup:sections.employee.addresses.description")}
                      bullets={t("helpPopup:sections.employee.addresses.bullets", { returnObjects: true }) as string[]}
                    />
                  }
                />

                <div className={defaultStyle.divisionBar}></div>

                <EmployeePermissions value={permissions} onChange={setPermissions} disabled={canEdite} />

                <div className={defaultStyle.divisionBar}></div>

                <div className={defaultStyle.buttonsController}>
                  <CanView permission={Permissions.CanEditeEmployee}>
                    <Button
                      label={t("buttons:button_save")}
                      typeButton="submit"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGSaveColor}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.employees.employeePupop.button.save"
                    />
                  </CanView>

                  {isEditeMode && employee && !employee.isMaster && (
                    <>
                      <GetSpace right={5} />

                      <Button
                        label={t("buttons:button_register")}
                        typeButton="button"
                        type="primary"
                        size="medium"
                        bgColor={THEME.colors.buttonBGRegisterColor}
                        onClick={handleEnableRegister}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.employees.employeePupop.button.register"
                      />

                      <GetSpace right={5} />

                      <Button
                        label={t("buttons:button_change_password")}
                        typeButton="button"
                        type="primary"
                        size="medium"
                        bgColor={THEME.colors.buttonBGRemoveColor}
                        onClick={() => setChangePassword(true)}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.employees.employeePupop.button.changePassword"
                      />
                    </>
                  )}
                </div>
              </>
            )}
          </form>
        </div>
      </Animate>

      {enableChangePassword && <ChangePasswordPopup employee={employee} onClonse={() => setChangePassword(false)} />}

      {isRegisterEnable && (
        <ViewRegister id={employee?.id!} typeEntity="EmployeeEntityId" onClose={handleDisableRegister} />
      )}
    </div>
  )
}
